import { PropsWithChildren, useEffect, useState, useCallback } from 'react'
import LoadingContent from 'components/LoadingContent'
import { Answer, CategoryGroup, Schema, Variable } from 'typings/filters'
import { Variables } from 'reducers/filtering/selectors'
import { useDispatch } from 'react-redux'
import { setFilterValues } from 'reducers/filtering/filterValues'
import { useFetchFilterValues } from 'hooks/useFetchFilterValues'
import { useFilters } from 'hooks/useFilters'
import pako from 'pako'

const isBase64 = (str: string) => {
  try {
    return btoa(atob(str)) === str
  } catch (err) {
    return false
  }
}

export const decompressData = (compressedData: string) => {
  if (compressedData && isBase64(compressedData)) {
    const binaryString = atob(compressedData)
    const len = binaryString.length
    const bytes = new Uint8Array(len)
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }
    const decompressedData = pako.inflate(bytes, { to: 'string' })
    return { filters: JSON.parse(decompressedData) }
  }
}

export const LoadFilters = ({ children }: PropsWithChildren<any>) => {
  const [shouldDisplayChildren, setShouldDisplayChildren] = useState<boolean>(false)
  const dispatch = useDispatch()

  const { data: countryCIE, loading: loadingCountryCIE } = useFetchFilterValues(
    Variables.COUNTRY,
    Schema.CIE
  )
  const { data: productGroupCIE, loading: loadingProductGroupCIE } = useFetchFilterValues(
    Variables.PRODUCT_GROUP,
    Schema.CIE
  )
  const { data: liveCIE, loading: loadingLiveCIE } = useFetchFilterValues(
    Variables.TIME_FRAME_LIVE,
    Schema.CIE
  )
  const { data: yearlyCIE, loading: loadingYearlyCIE } = useFetchFilterValues(
    Variables.TIME_FRAME_YEARLY,
    Schema.CIE
  )
  const { data: reviewFilters, loading: reviewFiltersLoading } = useFilters(Schema.CIE_REVIEW)
  const finalReviewFilters = decompressData(reviewFilters?.filters)
  const loading = [
    loadingCountryCIE,
    loadingProductGroupCIE,
    loadingLiveCIE,
    loadingYearlyCIE,
    reviewFiltersLoading,
  ].some(Boolean)

  const dispatchFilterValues = useCallback(
    (variableId: number, schema: Schema, data: any, reviewFiltersMap?: Map<number, Variable>) => {
      const values =
        schema === Schema.CIE_REVIEW
          ? reviewFiltersMap?.get(variableId)?.values
          : data?.filterValues
      dispatch(
        setFilterValues({
          variableId: variableId.toString() + schema,
          values: values as Answer[],
        })
      )
    },
    [dispatch]
  )

  useEffect(() => {
    if (loading) {
      return
    }
    const reviewFiltersMap = new Map<number, Variable>()
    finalReviewFilters?.filters.forEach((obj: CategoryGroup) => {
      obj.categories.forEach((category) => {
        category.variables.forEach((variable) => {
          reviewFiltersMap.set(variable.variable_id, variable)
        })
      })
    })
    dispatchFilterValues(Variables.COUNTRY, Schema.CIE, countryCIE)
    dispatchFilterValues(Variables.PRODUCT_GROUP, Schema.CIE, productGroupCIE)
    dispatchFilterValues(Variables.TIME_FRAME_LIVE, Schema.CIE, liveCIE)
    dispatchFilterValues(Variables.TIME_FRAME_YEARLY, Schema.CIE, yearlyCIE)
    dispatchFilterValues(Variables.COUNTRY, Schema.CIE_REVIEW, null, reviewFiltersMap)
    dispatchFilterValues(Variables.PRODUCT_GROUP, Schema.CIE_REVIEW, null, reviewFiltersMap)
    dispatchFilterValues(Variables.TIME_FRAME_LIVE, Schema.CIE_REVIEW, null, reviewFiltersMap)
    dispatchFilterValues(Variables.TIME_FRAME_YEARLY, Schema.CIE_REVIEW, null, reviewFiltersMap)

    setShouldDisplayChildren(true)
  }, [loading, countryCIE, productGroupCIE, liveCIE, yearlyCIE, dispatchFilterValues])

  if (loading) {
    return <LoadingContent />
  }

  return <>{shouldDisplayChildren && children}</>
}
